import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';
import useDeepCompareEffect from 'use-deep-compare-effect';

import {
  Button,
  Notification,
  Typography,
} from 'redpoints-front-components/index';
import { historyHelper, routeBuilder } from 'redpoints-front-common/helpers';

import {
  incidentDetailsSaveContentLink,
  incidentDetailsSaveDetectionLink,
  incidentDetailsSetForceValidation,
} from '../../../actions/incidentDetailsActions';
import {
  getIncidentDetailsContentLinkIsValid,
  getIncidentDetailsContentLinkIsValidating,
  getIncidentDetailsContentLinkSaveServiceModel,
  getIncidentDetailsContentLinkValidationInitialState,
  getIncidentDetailsDetectionLinkIsValid,
  getIncidentDetailsDetectionLinkIsValidating,
  getIncidentDetailsDetectionLinkSaveServiceModel,
  getIncidentDetailsDetectionLinkValidationInitialState,
  getIncidentDetailsHasContentLinkData,
  getIncidentDetailsSaveLinksIsLoading,
  getIncidentDetailsSaveLinksIsSuccess,
} from '../../../reducers/reporting/incidentDetails';
import { ROUTE_PATHS } from '../../../config/constants';
import { translate } from '../../../helpers/languageHelper';

const getHasChanges = linkServiceModel => {
  const hasChanges =
    !isEqual(
      linkServiceModel.evidence.modified,
      linkServiceModel.evidence.original
    ) ||
    !isEqual(
      linkServiceModel.account.modified,
      linkServiceModel.account.original
    ) ||
    (linkServiceModel.account.modified.accountId &&
      linkServiceModel.account.initialLinkAccountId !==
        linkServiceModel.account.modified.accountId) ||
    !isEqual(
      linkServiceModel.seller.modified,
      linkServiceModel.seller.original
    ) ||
    !isEqual(
      linkServiceModel.linkData.modified,
      linkServiceModel.linkData.original
    );
  return hasChanges;
};

const redirectToAllIncidents = context => {
  const location = {
    pathname: routeBuilder(context, ROUTE_PATHS.REPORTING_ALL_INCIDENTS),
  };
  historyHelper.push(location);
};

const IncidentDetailsActions = ({
  contentLinkInitialValidationState,
  contentLinkServiceModel,
  context,
  detectionLinkInitialValidationState,
  detectionLinkServiceModel,
  hasContentLinkData,
  incidentDetailsSaveContentLink,
  incidentDetailsSaveDetectionLink,
  incidentDetailsSetForceValidation,
  isSaveLinksLoading,
  isSaveLinksSuccess,
  isValidatingContentLink,
  isValidatingDetectionLink,
  isValidContentLink,
  isValidDetectionLink,
}) => {
  const hasChangesContentLink = getHasChanges(contentLinkServiceModel);
  const hasChangesDetectionLink = getHasChanges(detectionLinkServiceModel);

  const [
    hasContentLinkValidationStarted,
    setHasContentLinkValidationStarted,
  ] = useState(false);
  const [
    hasDetectionLinkValidationStarted,
    setHasDetectionLinkValidationStarted,
  ] = useState(false);

  useEffect(() => {
    if (isSaveLinksSuccess) {
      redirectToAllIncidents(context);
    }
  }, [context, isSaveLinksSuccess]);

  useEffect(() => {
    if (isValidatingContentLink) {
      setHasContentLinkValidationStarted(true);
    }
  }, [isValidatingContentLink]);

  useEffect(() => {
    if (isValidatingDetectionLink) {
      setHasDetectionLinkValidationStarted(true);
    }
  }, [isValidatingDetectionLink]);

  useDeepCompareEffect(() => {
    const isContentLinkValidationFinished =
      hasContentLinkValidationStarted && !isValidatingContentLink;

    const isDetectionLinkValidationFinished =
      hasDetectionLinkValidationStarted && !isValidatingDetectionLink;

    if (hasContentLinkData) {
      if (
        isContentLinkValidationFinished &&
        isDetectionLinkValidationFinished
      ) {
        setHasContentLinkValidationStarted(false);
        setHasDetectionLinkValidationStarted(false);

        if (isValidContentLink && isValidDetectionLink) {
          incidentDetailsSaveContentLink({ contentLinkServiceModel });
          incidentDetailsSaveDetectionLink({ detectionLinkServiceModel });
        } else {
          if (isValidContentLink) {
            Notification.error({
              message: translate(
                'reporting.incidentDetails.errors.validatingDetectionLink'
              ),
            });
          } else if (isValidDetectionLink) {
            Notification.error({
              message: translate(
                'reporting.incidentDetails.errors.validatingContentLink'
              ),
            });
          } else {
            Notification.error({
              message: translate(
                'reporting.incidentDetails.errors.validatingBothLinks'
              ),
            });
          }
        }
      }
    } else {
      if (isDetectionLinkValidationFinished) {
        setHasDetectionLinkValidationStarted(false);

        if (isValidDetectionLink) {
          incidentDetailsSaveDetectionLink({ detectionLinkServiceModel });
        } else {
          Notification.error({
            message: translate(
              'reporting.incidentDetails.errors.validatingDetectionLink'
            ),
          });
        }
      }
    }
  }, [
    contentLinkServiceModel,
    detectionLinkServiceModel,
    hasContentLinkValidationStarted,
    hasDetectionLinkValidationStarted,
    incidentDetailsSaveContentLink,
    incidentDetailsSaveDetectionLink,
    isValidContentLink,
    isValidDetectionLink,
    isValidatingContentLink,
    isValidatingDetectionLink,
    hasContentLinkData,
  ]);

  const handleCancel = () => redirectToAllIncidents(context);

  const handleSave = () =>
    incidentDetailsSetForceValidation({
      contentLinkInitialValidationState,
      detectionLinkInitialValidationState,
    });

  const hasChanges = hasChangesContentLink || hasChangesDetectionLink;
  const isValidating =
    (hasContentLinkData && isValidatingContentLink) ||
    isValidatingDetectionLink;

  return (
    <div>
      <Typography>
        {translate('reporting.incidentDetails.actions.disclaimer')}
      </Typography>
      <div className="incident-details-actions-container">
        <Button
          aria-label="button-cancel"
          disabled={isValidating || isSaveLinksLoading}
          onClick={handleCancel}
          type="default"
        >
          {translate('reporting.incidentDetails.actions.cancel')}
        </Button>
        <Button
          aria-label="button-save"
          disabled={isValidating || isSaveLinksLoading || !hasChanges}
          onClick={handleSave}
          type="primary"
        >
          {translate('reporting.incidentDetails.actions.save')}
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  contentLinkInitialValidationState: getIncidentDetailsContentLinkValidationInitialState(
    state
  ),
  contentLinkServiceModel: getIncidentDetailsContentLinkSaveServiceModel(state),
  detectionLinkInitialValidationState: getIncidentDetailsDetectionLinkValidationInitialState(
    state
  ),
  detectionLinkServiceModel: getIncidentDetailsDetectionLinkSaveServiceModel(
    state
  ),
  isSaveLinksLoading: getIncidentDetailsSaveLinksIsLoading(state),
  isSaveLinksSuccess: getIncidentDetailsSaveLinksIsSuccess(state),
  isValidContentLink: getIncidentDetailsContentLinkIsValid(state),
  isValidDetectionLink: getIncidentDetailsDetectionLinkIsValid(state),
  isValidatingContentLink: getIncidentDetailsContentLinkIsValidating(state),
  isValidatingDetectionLink: getIncidentDetailsDetectionLinkIsValidating(state),
  hasContentLinkData: getIncidentDetailsHasContentLinkData(state),
});

export default connect(mapStateToProps, {
  incidentDetailsSaveContentLink,
  incidentDetailsSaveDetectionLink,
  incidentDetailsSetForceValidation,
})(IncidentDetailsActions);
